import type { EntityOutboxEntityType, EntityOutboxEntry } from '../db'
import { getOfflineDb } from '../dbFactory'
import { isPermissionBlockedEntry } from '../../screens/crewMode/crewPermissionBlocked'

// Findings #172 (validation day 1, 2026-10-14): the server refused one queued
// record (a count dated before its lot reached the unit). The queue sends in
// order, so 13 later changes — unit plans, feed items, costs — waited behind it
// for hours while the only message sat in Editar instalación, in English. This
// finds that record and says, in Spanish, what it is and where to fix it.

/** The first refused change in the queue and how many changes wait behind it. */
export type RefusedChange = {
  entry: EntityOutboxEntry
  waitingBehind: number
}

// 403 has its own notice (findings #123); 404 / 408 / 429 are not "the server
// said no to this record". Network failures carry no status.
const isRefusedEntry = (entry: EntityOutboxEntry): boolean => {
  const status = entry.lastErrorStatus
  if (typeof status !== 'number' || status < 400 || status >= 500) return false
  if (status === 401 || status === 404 || status === 408 || status === 429) return false
  return !isPermissionBlockedEntry(entry)
}

export const findRefusedChange = (entries: readonly EntityOutboxEntry[]): RefusedChange | null => {
  const ordered = entries
    .filter((entry) => typeof entry.seq === 'number')
    .slice()
    .sort((left, right) => (left.seq as number) - (right.seq as number))
  const index = ordered.findIndex(isRefusedEntry)
  if (index < 0) return null
  return { entry: ordered[index], waitingBehind: ordered.length - index - 1 }
}

export const readRefusedChange = async (): Promise<RefusedChange | null> => {
  try {
    return findRefusedChange(await getOfflineDb().entityOutbox.toArray())
  } catch {
    return null
  }
}

/**
 * Ids of every refused record of these types, plus the production row each one
 * belongs to (Biomasa lists rows), for a "No enviado" tag on its row.
 */
export const readRefusedRowIds = async (entityTypes: readonly EntityOutboxEntityType[]): Promise<Set<string>> => {
  try {
    const entries = await getOfflineDb().entityOutbox.toArray()
    const ids = new Set<string>()
    entries
      .filter((entry) => entityTypes.includes(entry.entityType) && isRefusedEntry(entry))
      .forEach((entry) => {
        ids.add(entry.entityId)
        const payload = entry.payload as { productionRowId?: unknown } | undefined
        if (typeof payload?.productionRowId === 'string' && payload.productionRowId.trim()) ids.add(payload.productionRowId.trim())
      })
    return ids
  } catch {
    return new Set()
  }
}

export type RefusedReason =
  | { kind: 'unitMismatch' }
  | { kind: 'lotNotActive' }
  | { kind: 'lotNotFound' }
  | { kind: 'other'; message: string }

/** The server's guard messages are English; map the known ones (assertLotBelongsToProductionUnitAt). */
export const classifyRefusedReason = (message: string | undefined): RefusedReason => {
  const text = (message ?? '').trim()
  if (/was assigned to a different production unit/i.test(text)) return { kind: 'unitMismatch' }
  if (/is not currently active/i.test(text)) return { kind: 'lotNotActive' }
  if (/lot .* was not found/i.test(text)) return { kind: 'lotNotFound' }
  return { kind: 'other', message: text }
}

/** Where in the office the record can be deleted or corrected. */
export const REFUSED_CHANGE_SCREEN: Record<EntityOutboxEntityType, string> = {
  farm: '/edit-farm',
  productionUnit: '/edit-farm',
  lot: '/lots',
  lotMoveCommand: '/lots',
  lotTransferCommand: '/lots',
  lotSplitCommand: '/lots',
  lotMergeCommand: '/lots',
  waterLog: '/water',
  productionRecord: '/database',
  feedLog: '/feed',
  mortalityLog: '/biomass',
  weightSampleLog: '/biomass',
  fishStockLog: '/biomass',
  harvestLog: '/biomass',
  lotCostEvent: '/costs',
  inventoryItem: '/inventory',
  task: '/tasks',
  mortalityMatrix: '/mortality-matrix',
  sfrMatrix: '/sfr'
}

const readString = (record: Record<string, unknown>, key: string): string => {
  const value = record[key]
  return typeof value === 'string' ? value.trim() : ''
}

/** The record's own details, read from what the queue holds. */
export const readRefusedChangeDetails = (entry: EntityOutboxEntry): {
  productionUnitId: string
  farmId: string
  lotLabel: string
  recordedAt: string
  name: string
} => {
  const payload = entry.payload && typeof entry.payload === 'object' && !Array.isArray(entry.payload)
    ? entry.payload as Record<string, unknown>
    : {}
  return {
    productionUnitId: readString(payload, 'productionUnitId'),
    farmId: readString(payload, 'farmId'),
    lotLabel: readString(payload, 'lot') || readString(payload, 'lotKey'),
    recordedAt: readString(payload, 'recordedAt') || readString(payload, 'recordDate'),
    name: readString(payload, 'name')
  }
}
